import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Task, TASK_STATUS_CONFIG, TASK_PRIORITY_CONFIG, taskIdToNato } from '../../types/tasks';
import { getTasks } from '../../services/api';

interface TaskSearchPanelProps {
  onSelect: (task: Task) => void;
  onClose: () => void;
  excludeTaskIds?: string[];
  title?: string;
  placeholder?: string;
}

const TaskSearchPanel: React.FC<TaskSearchPanelProps> = ({
  onSelect,
  onClose,
  excludeTaskIds = [],
  title,
  placeholder,
}) => {
  const { t } = useTranslation('tasks');
  const resolvedTitle = title ?? t('search.title');
  const resolvedPlaceholder = placeholder ?? t('search.placeholder');
  const [searchQuery, setSearchQuery] = useState('');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Load all tasks on mount
  useEffect(() => {
    const loadTasks = async () => {
      try {
        const data = await getTasks({ include_dependencies: false });
        // Filter out excluded tasks
        const available = data.filter(t => !excludeTaskIds.includes(t.id));
        setTasks(available);
        setFilteredTasks(available);
      } catch (err) {
        console.error('Failed to load tasks:', err);
      } finally {
        setLoading(false);
      }
    };
    loadTasks();
  }, [excludeTaskIds]);

  // Filter tasks based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTasks(tasks);
      setSelectedIndex(0);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = tasks.filter(task => {
      // Search by ID (exact or partial)
      if (task.id.toLowerCase().includes(query)) return true;
      // Search by NATO phonetic (e.g., "alpha" matches tasks with A)
      if (taskIdToNato(task.id).toLowerCase().includes(query)) return true;
      // Search by title
      if (task.title.toLowerCase().includes(query)) return true;
      // Search by description
      if (task.description?.toLowerCase().includes(query)) return true;
      return false;
    });

    setFilteredTasks(filtered);
    setSelectedIndex(0);
  }, [searchQuery, tasks]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && filteredTasks.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex, filteredTasks.length]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredTasks.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredTasks[selectedIndex]) {
          onSelect(filteredTasks[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [filteredTasks, selectedIndex, onSelect, onClose]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.task-search-panel')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div className="task-search-panel fixed inset-0 z-[60] flex items-start justify-center pt-[10vh] bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">{resolvedTitle}</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Input */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={resolvedPlaceholder}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                text-gray-900 placeholder-gray-400"
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {t('search.hint')}
          </p>
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-[400px] overflow-y-auto"
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('search.loading')}</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500">
                {searchQuery ? t('search.noResults') : t('search.noAvailable')}
              </p>
            </div>
          ) : (
            filteredTasks.map((task, index) => {
              const statusConfig = TASK_STATUS_CONFIG[task.status];
              const priorityConfig = TASK_PRIORITY_CONFIG[task.priority];
              const isSelected = index === selectedIndex;

              return (
                <div
                  key={task.id}
                  onClick={() => onSelect(task)}
                  className={`
                    px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0
                    transition-colors
                    ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}
                  `}
                >
                  <div className="flex items-start gap-3">
                    {/* Task ID */}
                    <div className="flex-shrink-0">
                      <span className="font-mono text-sm font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                        {task.id}
                      </span>
                    </div>

                    {/* Task Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 truncate">{task.title}</h4>
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-500 line-clamp-1 mb-1">
                          {task.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                          {statusConfig.icon} {t(`status.${task.status}`)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                          {t(`priority.${task.priority}`)}
                        </span>
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="flex-shrink-0 text-blue-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* NATO phonetic hint */}
                  <p className="mt-1 text-xs text-gray-400 font-mono pl-[52px]">
                    {taskIdToNato(task.id)}
                  </p>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center gap-4">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px]">↑</kbd>
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px]">↓</kbd>
            {t('search.keyboard.navigate')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px]">Enter</kbd>
            {t('search.keyboard.select')}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[10px]">Esc</kbd>
            {t('search.keyboard.close')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default TaskSearchPanel;
